import React, { useEffect, useState } from 'react';
import {
  Dialog,
  Box,
  Typography,
  Stack,
  Divider,
  Zoom,
  Button,
} from '@mui/material';

// Helper to format labels (no dots now)
const formatLabel = (label) => label.trim();

/**
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - breakdown: Array<{ label: string, points: number }>
 *  - streakMultiplier: number
 *  - totalPoints: number
 */
export function PointsEarnedPopup({
  open,
  onClose,
  breakdown,
  streakMultiplier,
  totalPoints,
}) {
  const [displayValues, setDisplayValues] = useState(
    breakdown?.map(() => 0) || []
  );
  const [displayTotal, setDisplayTotal] = useState(0);
  const [showMultiplier, setShowMultiplier] = useState(false);

  useEffect(() => {
    if (!open || !breakdown) return;

    let cancelled = false;

    // reset everything
    setDisplayValues(breakdown.map(() => 0));
    setDisplayTotal(0);
    setShowMultiplier(false);

    const animateRow = (index) => {
      if (cancelled) return;
      if (index >= breakdown.length) {
        // done with rows, show streak, then total
        setShowMultiplier(true);
        setTimeout(() => {
          if (!cancelled) animateTotal();
        }, 350);
        return;
      }

      const target = breakdown[index].points ?? 0;
      if (target === 0) {
        setDisplayValues((prev) => {
          const copy = [...prev];
          copy[index] = 0;
          return copy;
        });
        animateRow(index + 1);
        return;
      }

      let current = 0;
      const step = Math.max(1, Math.floor(target / 40)); // ~40 frames

      const interval = setInterval(() => {
        if (cancelled) {
          clearInterval(interval);
          return;
        }

        current += step;
        if (current >= target) {
          current = target;
          clearInterval(interval);
          setDisplayValues((prev) => {
            const copy = [...prev];
            copy[index] = current;
            return copy;
          });
          // move to next row
          setTimeout(() => animateRow(index + 1), 120);
        } else {
          setDisplayValues((prev) => {
            const copy = [...prev];
            copy[index] = current;
            return copy;
          });
        }
      }, 16); // ~60fps
    };

    const animateTotal = () => {
      const target = totalPoints ?? 0;
      let current = 0;
      const step = Math.max(1, Math.floor(target / 60));

      const interval = setInterval(() => {
        if (cancelled) {
          clearInterval(interval);
          return;
        }

        current += step;
        if (current >= target) {
          current = target;
          clearInterval(interval);
        }
        setDisplayTotal(current);
      }, 16);
    };

    animateRow(0);

    return () => {
      cancelled = true;
    };
  }, [open, breakdown, totalPoints]);


  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          background: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <Box
        sx={{
          px: 4,
          py: 3,
          borderRadius: 3,
          border: '4px solid',
          borderColor: 'secondary.main',
          backgroundColor: 'background.paper',
          boxShadow: (theme) => `0 0 20px ${theme.palette.secondary.main}55`,
          color: 'secondary.main',
          width: { xs: 300, sm: 360, md: 420 }, // fixed width so it doesn't grow
        }}
      >
        <Stack spacing={2.5} alignItems="stretch">
          {/* Title */}
          <Box textAlign="center">
            <Typography
              variant="h5"
              sx={{
                color: 'primary.main',
                fontFamily: 'inherit',
                textTransform: 'uppercase',
                lineHeight: 1.2,
              }}
            >
              Points
            </Typography>
            <Typography
              variant="h5"
              sx={{
                color: 'primary.main',
                fontFamily: 'inherit',
                textTransform: 'uppercase',
                lineHeight: 1.2,
              }}
            >
              Earned
            </Typography>
            <Box
              sx={{
                mt: 1,
                height: 4,
                backgroundColor: 'green.main',
              }}
            />
          </Box>

          {/* Breakdown rows */}
          <Stack spacing={1.2}>
            {breakdown.map((row, idx) => (
              <Typography
                key={row.label + idx}
                variant='body'
                sx={{
                  fontFamily: 'inherit',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  color: 'secondary.main',

                }}
              >
                {formatLabel(row.label)}
                <span
                  style={{
                    display: 'inline-block',
                    minWidth: '5ch', // reserve space so more digits don't shift things
                    textAlign: 'right',
                  }}
                >
                  {displayValues[idx] ?? 0}
                </span>
              </Typography>
            ))}
          </Stack>

          {/* Divider line */}
          <Divider
            sx={{
              borderColor: 'green.main',
              borderBottomWidth: 4,
              opacity: 1,
            }}
          />

          {/* Streak multiplier */}
          <Box
            sx={{
              height: 40, // reserve vertical space so popup height doesn't change
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Zoom in={showMultiplier}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Typography
                  sx={{ fontFamily: 'inherit', fontSize: 16 }}
                  component="span"
                >
                  🔥
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'inherit',
                    fontSize: 14,
                    color: 'secondary.main',
                  }}
                >
                  x {streakMultiplier}
                </Typography>
              </Box>
            </Zoom>
          </Box>

          {/* Divider line */}
          <Divider
            sx={{
              borderColor: 'green.main',
              borderBottomWidth: 4,
              opacity: 1,
            }}
          />

          {/* Total */}
          <Typography
            variant='body'
            sx={{
              fontFamily: 'inherit',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              color: 'secondary.main',
              whiteSpace: 'nowrap',
            }}
          >
            <span>{formatLabel('Total')}</span>
            <span
              style={{
                display: 'inline-block',
                minWidth: '6ch', // a bit more room for the big number
                textAlign: 'right',
              }}
            >
              {displayTotal}
            </span>
          </Typography>

          <Box textAlign="center" mt={1}>
            <Button
              variant="text"
              onClick={onClose}
              sx={{
                fontFamily: 'inherit',
                fontSize: 10,
                color: 'primary.main',
                textTransform: 'uppercase',
              }}
            >
              Continue
            </Button>
          </Box>
        </Stack>
      </Box>
    </Dialog>
  );
}
